/**
 * Avaliação pós-jogo.
 *
 * A tela reforça o anonimato em três momentos: no aviso do topo, na ausência
 * de qualquer indicação de "quem já te avaliou" e na confirmação final. Numa
 * pelada de amigos, a percepção de que o voto é secreto importa tanto quanto a
 * garantia técnica — sem ela, todo mundo dá 4 estrelas para evitar
 * constrangimento.
 */

import { CheckCircle2, EyeOff, Hand, Timer } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/layout';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  PositionBadge,
  Skeleton,
  StarRating,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useBallot, useSubmitRatings } from '@/lib/queries';

export function RatePage() {
  const { gameId = '' } = useParams();
  const navigate = useNavigate();

  const ballot = useBallot(gameId);
  const submit = useSubmitRatings(gameId);

  const [scores, setScores] = useState<Record<string, number>>({});
  const [done, setDone] = useState(false);

  const pending = ballot.data?.targets.filter((target) => !target.submitted) ?? [];
  const rated = Object.keys(scores).length;

  async function handleSubmit() {
    const ratings = Object.entries(scores).map(([ballotToken, score]) => ({
      ballotToken,
      score,
    }));
    if (ratings.length === 0) return;

    await submit.mutateAsync(ratings);
    setDone(true);
  }

  if (ballot.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (ballot.error) {
    return (
      <>
        <PageHeader title="Avaliar jogadores" back={{ to: `/partidas/${gameId}`, label: 'Partida' }} />
        <Card>
          <EmptyState
            icon={Timer}
            title="Avaliações indisponíveis"
            description={
              ballot.error instanceof ApiError
                ? ballot.error.message
                : 'Não foi possível carregar as avaliações.'
            }
            action={
              <Button variant="secondary" onClick={() => navigate(`/partidas/${gameId}`)}>
                Voltar
              </Button>
            }
          />
        </Card>
      </>
    );
  }

  if (done) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 py-10 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-ok-soft">
            <CheckCircle2 className="h-9 w-9 text-ok" aria-hidden />
          </span>

          <h2 className="text-xl font-extrabold">Avaliações enviadas!</h2>

          <p className="max-w-md text-sm text-ink-2">
            As notas entram no cálculo quando o prazo de avaliação fechar. Ninguém — nem os
            administradores — consegue ver quem avaliou quem.
          </p>

          <Button size="lg" onClick={() => navigate(`/partidas/${gameId}`)}>
            Concluir
          </Button>
        </div>
      </Card>
    );
  }

  if (pending.length === 0) {
    return (
      <>
        <PageHeader title="Avaliar jogadores" back={{ to: `/partidas/${gameId}`, label: 'Partida' }} />
        <Card>
          <EmptyState
            icon={CheckCircle2}
            title="Você já avaliou todo mundo"
            description="Obrigado! Suas notas ajudam a equilibrar os próximos times."
            action={
              <Button variant="secondary" onClick={() => navigate(`/partidas/${gameId}`)}>
                Voltar
              </Button>
            }
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Como eles jogaram?"
        subtitle={`${rated} de ${pending.length} avaliados`}
        back={{ to: `/partidas/${gameId}`, label: 'Partida' }}
      />

      {/* O aviso de anonimato é a primeira coisa que a pessoa lê. */}
      <div className="mb-6 rounded-card bg-brand-soft p-5">
        <p className="flex items-center gap-2 font-bold text-brand">
          <EyeOff className="h-5 w-5" aria-hidden />
          Avaliação 100% anônima
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-2">
          O jogador vê apenas a nota final dele, nunca as notas individuais nem quem avaliou. O
          administrador também não tem acesso — o vínculo entre avaliador e avaliado é apagado do
          sistema quando o prazo fecha.
        </p>
      </div>

      <div className="space-y-3">
        {pending.map((target) => (
          <Card key={target.ballotToken}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <Avatar
                  name={target.name}
                  src={target.avatarUrl}
                  size={42}
                  badge={target.position === 'GOALKEEPER' ? Hand : undefined}
                />
                <div className="min-w-0">
                  <p className="truncate font-semibold">{target.name}</p>
                  <div className="mt-1">
                    <PositionBadge
                      position={target.position}
                      canPlayGoalkeeper={target.canPlayGoalkeeper}
                    />
                  </div>
                </div>
              </div>

              <StarRating
                value={scores[target.ballotToken] ?? 0}
                size={26}
                showValue={false}
                interactive
                onChange={(value) =>
                  setScores((current) => ({ ...current, [target.ballotToken]: value }))
                }
              />
            </div>
          </Card>
        ))}
      </div>

      <p className="mt-4 text-center text-[13px] text-ink-3">
        Clique duas vezes na mesma estrela para dar meia estrela.
      </p>

      <div className="sticky bottom-4 mt-6">
        <Card className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[13px] text-ink-2">
            {rated === 0
              ? 'Selecione ao menos uma nota para enviar.'
              : `${rated} avaliação${rated > 1 ? 'ões' : ''} pronta${rated > 1 ? 's' : ''}.`}
          </p>

          <div className="flex items-center gap-3">
            {submit.error ? (
              <span className="text-[13px] text-bad">
                {submit.error instanceof ApiError ? submit.error.message : 'Erro ao enviar.'}
              </span>
            ) : null}

            <Button
              size="lg"
              disabled={rated === 0}
              loading={submit.isPending}
              onClick={handleSubmit}
            >
              Enviar avaliações
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
}
